<script lang="ts" setup>
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import OverlayHeader from '@/components/overlays/OverlayHeader.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import SkrHeading from '@/components/SkrHeading.vue'
import IllustrationSomeoneSigning from '@/components/identification/IllustrationSomeoneSigning.vue'
import IllustrationReadyToSign from '@/components/identification/IllustrationReadyToSign.vue'
import useIdentificationTheme from '@/composables/useIdentificationTheme.ts'

const props = withDefaults(
  defineProps<{
    processedRequests?: string[]
    lockData?: SignLockData
    closeOnAction?: boolean
    closable?: boolean
    isXQES?: boolean
    invitingBusinessId: string
  }>(),
  {
    processedRequests: (): string[] => [],
    lockData: (): SignLockData => ({
      signatureRequestId: '',
      firstName: '',
      lastName: '',
      emailAddress: '',
    }),
    closeOnAction: false,
    closable: true,
    isXQES: false,
  }
)

const emit = defineEmits<{
  action: [type: OverlayAction, data?: unknown]
  close: []
}>()

const { t } = useI18n()

const { isDirectSign } = storeToRefs(useDirectSignStore())

const { rootStyle } = useIdentificationTheme(() => props.invitingBusinessId)

const lockingUserName = computed(() => {
  if (props.lockData?.firstName && props.lockData?.lastName) {
    return `${props.lockData.firstName} ${props.lockData.lastName}`
  } else if (props.lockData?.emailAddress) {
    return props.lockData?.emailAddress
  } else {
    return t('global.someone_else')
  }
})
const isIdentityAvailable = computed(
  () => (props.lockData?.firstName && props.lockData?.lastName) || props.lockData?.emailAddress
)

const isLocked = ref(Boolean(props.lockData))

const lockInterval = ref(0)

const { srRepository, directSrRepository } = useLegacyApi()

const checkLockStatus = async () => {
  const { locked } = isDirectSign.value
    ? await directSrRepository.checkLock(props.lockData!.signatureRequestId)
    : await srRepository.checkLock(props.lockData!.signatureRequestId)

  const wasLocked = isLocked.value
  isLocked.value = locked

  if (!isLocked.value && lockInterval.value) {
    clearInterval(lockInterval.value)
    lockInterval.value = 0
  }

  if (wasLocked && !locked) emit('action', 'unlock')
}

const sign = () => {
  if (props.closeOnAction) {
    emit('close')
    return
  }
  emit('action', 'retry', { id: props.lockData?.signatureRequestId })
}

onMounted(() => {
  checkLockStatus().then(() => {
    if (isLocked.value) {
      lockInterval.value = window.setInterval(checkLockStatus, 3000)
    }
  })
})

onBeforeUnmount(() => {
  if (lockInterval.value) {
    clearInterval(lockInterval.value)
    lockInterval.value = 0
  }
})
</script>

<template>
  <overlay-base :transition="false">
    <template #header>
      <overlay-header v-if="closable" @close="emit('close')" />
      <div v-else></div>
    </template>
    <div class="identification-typography" :style="rootStyle">
      <v-container class="fill-height overlay-container">
        <v-row justify="center">
          <v-col cols="12" md="6">
            <div class="d-flex flex-column align-center justify-center">
              <illustration-someone-signing v-if="isLocked" />
              <illustration-ready-to-sign v-else />
              <div class="mt-8 mb-12 text-center d-flex flex-column ga-8">
                <skr-heading level="1" data-cy="title" semantic>
                  <template v-if="isLocked">
                    <span class="name" :class="{ 'name--highlight': isIdentityAvailable }">{{ lockingUserName }}</span>
                    {{ ' ' + t('sign.locked.title_locked') }}
                  </template>
                  <template v-else>
                    {{ t('sign.locked.title_unlocked') }}
                  </template>
                </skr-heading>
                <p v-if="isLocked" class="subtitle-text" data-cy="subtitle">
                  {{ isXQES ? t('sign.locked.ciba_locked') : t('sign.locked.subtitle') }}
                </p>
              </div>
              <skr-spinner v-if="!closable" size="small" />
              <skr-button
                v-else
                color="primary-xqes"
                :disabled="isLocked"
                :loading="isLocked"
                size="xl"
                data-cy="action_button"
                :class="{ 'min-width-button': !isLocked }"
                @click="sign"
              >
                <v-icon start> custom:signature </v-icon>
                {{ t('global.sign_now') }}
              </skr-button>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </overlay-base>
</template>

<style lang="scss" scoped>
.min-width-button {
  min-width: 224px;
}
.name {
  word-break: break-word;
  overflow-wrap: break-word;
  display: inline;

  &--highlight {
    color: $c-grey-dark;
  }
}

.overlay-container {
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.subtitle-text {
  max-width: 70%;
  margin-inline: auto;
  font-size: 1rem;
  // Description text per Vlad's design (signing.html) uses --on-surface, not
  // the --on-surface-high reserved for the heading above it.
  color: rgb(var(--v-theme-on-surface-xqes));
}

:deep(.heading1) {
  font-size: 1.5rem;
}
</style>
